/**
 * Modal
 *
 * There is no special component for this. The dialog element is always on the
 * product page via the Product page layout, and it is populated and opened via Javascript, and then
 * emptied automatically when it closes.
 *
 * Usage (twig):
 *
 *     {# The name of the following variable is not important #}
 *     {% set modalContent %}
 *       Modal content goes here.
 *       Bring your own styles such as background color, padding, and max-width,
 *       which may mean including your own wrapper element.
 *       This can contain any HTML, and escaping is handled below.
 *     {% endset %}
 *
 *     {# The variable from above is used here.
 *      # We apply this via a filter rather than with an inline attribute
 *      # since this way escaping is handled for us. #}
 *     {% filter attr({ "data-content": modalContent "} %}
 *
 *       {# The following can be any element which supports a click handler,
 *        # but button is likely most appropriate.
 *        # It must have the `js-open-modal` class
 *        # to have the click handler applied. #}
 *       <button class="js-open-modal">
 *         Open modal
 *       </button>
 *
 *     %}
 *
 *  To enforce 16:9 aspect on the modal (and for its immediate child to then
 *  cover this), add `data-content-class="aspect-16/9"` to the button.
 */

// Find dialog element
const dialog = document.querySelector(
  "dialog#modal"
) as HTMLDialogElement | null;
if (dialog == null) throw new Error("No dialog element");
const contentElement = dialog.querySelector(
  "dialog#modal > div"
) as HTMLDivElement | null;
if (contentElement == null)
  throw new Error("No content div inside dialog element");

// Allow modal to close on backdrop click
dialog.addEventListener("click", (event) => {
  // Do nothing if some child was clicked
  if (event.target !== dialog) return;

  // Get box position
  const rect = dialog.getBoundingClientRect();

  // Do nothing if the click was inside
  if (
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom
  ) {
    return;
  }

  // Close dialog
  dialog.close();
});

// Attach handler to close button
document
  .querySelector("dialog#modal > button")
  ?.addEventListener("click", () => dialog.close());

// Empty dialog contents when it is closed
dialog.addEventListener("close", () => {
  contentElement.innerHTML = "";
  contentElement.className = "";
});

// Handle clicks on "open modal" buttons
for (const el of document.querySelectorAll(
  ".js-open-modal"
) as NodeListOf<HTMLElement>) {
  const content = el.dataset.content;
  if (content == null)
    console.warn("Element with .js-open-modal has no data-content", el);

  el.addEventListener("click", () => {
    if (content != null) {
      contentElement.innerHTML = content;
      if (el.dataset.contentClass)
        contentElement.className = el.dataset.contentClass;
    }
    dialog.showModal();
  });
}
